/**
 * Offline action queue — stores failed writes in IndexedDB and flushes when
 * the browser comes back online.
 *
 * Pragmatic scope: supports note creation for a lead today, but designed to
 * accept any `{kind, leadId, payload}` record and an appropriate sender.
 *
 * Storage: IndexedDB database `rl-offline-queue`, object store `actions`,
 * key-path `id` (autoincrement), indexed by `kind`.
 */

const DB_NAME = 'rl-offline-queue';
const DB_VERSION = 1;
const STORE = 'actions';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('kind', 'kind', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function enqueue(action) {
  const record = { ...action, created_at: new Date().toISOString() };
  await withStore('readwrite', (store) => store.add(record));
  notifyChange();
}

export async function listQueue(kind = null) {
  return withStore('readonly', (store) => {
    return new Promise((resolve) => {
      const items = [];
      const cursorReq = kind ? store.index('kind').openCursor(IDBKeyRange.only(kind)) : store.openCursor();
      cursorReq.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) { items.push(cursor.value); cursor.continue(); }
        else resolve(items);
      };
    });
  });
}

export async function removeFromQueue(id) {
  await withStore('readwrite', (store) => store.delete(id));
  notifyChange();
}

export async function queueSize(kind = null) {
  const items = await listQueue(kind);
  return items.length;
}

/**
 * Flush the queue by invoking `sender(action)` for each pending action.
 * `sender` must throw on failure (we keep the item for the next attempt).
 * Returns `{flushed, remaining}`.
 */
export async function flushQueue(sender) {
  const items = await listQueue();
  let flushed = 0;
  for (const item of items) {
    try {
      await sender(item);
      await removeFromQueue(item.id);
      flushed += 1;
    } catch (e) {
      // Stop on the first failure so we don't hammer a broken endpoint
      break;
    }
  }
  const remaining = await queueSize();
  return { flushed, remaining };
}

// ── simple pub/sub so UI can subscribe to queue-size changes ──
const listeners = new Set();
export function onQueueChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function notifyChange() {
  listeners.forEach((cb) => { try { cb(); } catch (e) { /* ignore */ } });
}
